import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { MotiView } from 'moti';
import { memo } from 'react';
import { DimensionValue, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { aiColors } from '../../../../ai-suite/theme';
import { SubtitledHeader } from '../../../components/SubtitledHeader';

/**
 * The practice scene while it opens (the customer's opening line is
 * written and voiced first): its header, the case's line, the customer's
 * first bubble and the controls, laid out as they will be. Back works.
 */
export const PracticeSceneSkeleton = memo(
  ({ onBack }: { onBack: () => void }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const bar = (width: DimensionValue, height: number) => (
      <View
        style={{
          width,
          height,
          borderRadius: rpx(6),
          backgroundColor: aiColors.skeleton,
        }}
      />
    );
    const round = (size: number) => (
      <View
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: aiColors.skeleton,
        }}
      />
    );
    return (
      <View style={{ flex: 1, backgroundColor: aiColors.background }}>
        <SubtitledHeader
          onBack={onBack}
          leading={round(rpx(36))}
          title={
            <View style={{ gap: rpx(6) }}>
              {bar(rpx(120), rpx(14))}
              {bar(rpx(170), rpx(10))}
            </View>
          }
        />
        <MotiView
          from={{ opacity: 1 }}
          animate={{ opacity: 0.55 }}
          transition={{ type: 'timing', duration: 800, loop: true }}
          style={{ flex: 1 }}
        >
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              paddingHorizontal: rpx(16),
              paddingVertical: rpx(12),
              borderBottomWidth: 1,
              borderBottomColor: aiColors.border,
              backgroundColor: aiColors.card,
            }}
          >
            {bar('55%', rpx(12))}
            {bar(rpx(48), rpx(12))}
          </View>
          <View style={{ flex: 1, padding: rpx(16), gap: rpx(16) }}>
            <View style={{ alignSelf: 'center' }}>
              {bar(rpx(120), rpx(24))}
            </View>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'flex-end',
                gap: rpx(8),
              }}
            >
              {round(rpx(32))}
              <View
                style={{
                  width: '70%',
                  gap: rpx(12),
                  borderRadius: rpx(22),
                  borderBottomLeftRadius: rpx(6),
                  padding: rpx(14),
                  backgroundColor: aiColors.card,
                }}
              >
                {bar('55%', rpx(16))}
                {bar('100%', rpx(12))}
                {bar('70%', rpx(12))}
              </View>
            </View>
          </View>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: rpx(8),
              paddingHorizontal: rpx(12),
              paddingTop: rpx(12),
              paddingBottom: insets.bottom + rpx(12),
              borderTopWidth: 1,
              borderTopColor: aiColors.border,
              backgroundColor: aiColors.card,
            }}
          >
            {round(rpx(40))}
            <View style={{ flex: 1 }}>{bar('100%', rpx(44))}</View>
          </View>
        </MotiView>
      </View>
    );
  },
);
